import { SettingsItemAutocomplete } from '@elipssolution/harfang';
import { useCallback, useState } from 'react';
import { v4 as uuid } from 'uuid';

import SettingsDialogPage from '../../../../components/SettingsDialogPage';
import { WidgetExternalApplicationType, WidgetWithKeyType } from '../../../../types/widget';
import { CecurityForm } from '../applications/Cecurity';
import { DigipharmacieSSOForm } from '../applications/DigipharmacieSSO';
import { FinthesisSSOForm } from '../applications/FinthesisSSO';
import { GenericForm } from '../applications/Generic';
import { MegSSOForm } from '../applications/MegSSO';
import { MySilaeForm } from '../applications/MySilae';
import { N2fForm } from '../applications/N2f';
import { SilaeSSOForm } from '../applications/SilaeSSO';
import { YoozForm } from '../applications/Yooz';

type ApplicationFormType = {
	type?: string;
	name: string;
	component: (props: {
		widgetWithKey: WidgetWithKeyType;
		application: WidgetExternalApplicationType;
		isNew: boolean;
		onSubmit: (application: WidgetExternalApplicationType) => void;
		onRemove: (application: WidgetExternalApplicationType) => void;
	}) => JSX.Element;
};

const defaultApplicationForm: ApplicationFormType = {
	type: undefined,
	name: 'Personnalisé',
	component: GenericForm,
};

const applicationForms: ApplicationFormType[] = [
	defaultApplicationForm,
	{
		type: 'cecurity',
		name: 'Cecurity',
		component: CecurityForm,
	},
	{
		type: 'digipharmacieSSO',
		name: 'Digipharmacie',
		component: DigipharmacieSSOForm,
	},
	{
		type: 'mySilae',
		name: 'MySilae',
		component: MySilaeForm,
	},
	{
		type: 'yooz',
		name: 'Yooz',
		component: YoozForm,
	},
	{
		type: 'n2f',
		name: 'N2F',
		component: N2fForm,
	},
	{
		type: 'finthesisSSO',
		name: 'Finthesis',
		component: FinthesisSSOForm,
	},
	{
		type: 'megSSO',
		name: 'MEG',
		component: MegSSOForm,
	},
	{
		type: 'silaeSSO',
		name: 'Silae',
		component: SilaeSSOForm,
	},
];

type ExternalApplicationFormSectionProps = {
	widgetWithKey: WidgetWithKeyType;
	application?: WidgetExternalApplicationType;
	onSubmit: (application: WidgetExternalApplicationType) => void;
	onRemove: (application: WidgetExternalApplicationType) => void;
	onBack: () => void;
};

const ExternalApplicationFormSection = ({
	widgetWithKey,
	application: initialApplication,
	onSubmit,
	onRemove,
	onBack,
}: ExternalApplicationFormSectionProps) => {
	const [application, setApplication] = useState<WidgetExternalApplicationType>(
		initialApplication ?? {
			key: uuid(),
			name: defaultApplicationForm.name,
			type: defaultApplicationForm.type,
		},
	);

	const selectedApplicationForm =
		applicationForms.find((applicationForm) => applicationForm.type === application.type) ?? defaultApplicationForm;

	const handleChangeType = ({ name, type } = defaultApplicationForm) =>
		setApplication({
			key: uuid(),
			name,
			type,
		});

	const externalApplicationsDataSource = useCallback(
		async (): Promise<{
			items: ApplicationFormType[];
			count: number;
		}> =>
			Promise.resolve({
				items: applicationForms,
				count: applicationForms.length,
			}),
		[],
	);

	return (
		<SettingsDialogPage title={application.name} onBack={onBack}>
			<SettingsItemAutocomplete
				dataSource={externalApplicationsDataSource}
				dataKey="type"
				description="Liste des applications disponibles."
				getOptionLabel={({ name }) => name}
				label="Type d'application"
				onChange={handleChangeType}
				value={selectedApplicationForm}
				disableClearable
			/>

			<selectedApplicationForm.component
				widgetWithKey={widgetWithKey}
				application={application}
				isNew={!initialApplication}
				onSubmit={onSubmit}
				onRemove={onRemove}
			/>
		</SettingsDialogPage>
	);
};

export default ExternalApplicationFormSection;
